/**
 * Post Credits
 *
 * Turns a post's `authors` + `credits` frontmatter into the provenance-footer
 * sentences rendered at the end of every DCYFR-drafted post (and the lighter
 * "Feedback from DCYFR" line on Drew-solo posts that used the writing coach).
 *
 * Pure text-building, kept separate from the rendering component so the
 * wording rules are unit-testable without React.
 *
 * @see openspec dcyfr-2027-three-author-calendar (rei-workspace repo) — Credit
 *      section: "DCYFR always gets credit... providers and partners get
 *      credit... house rule for AI images: name the model, never
 *      'illustrated' or 'painted'."
 */

import type { Post, PostCredits } from '@/data/posts';

const DEFAULT_AUTHORS: readonly string[] = ['dcyfr'];

function resolveAuthors(authors: string[] | undefined): string[] {
  return authors && authors.length > 0 ? authors : [...DEFAULT_AUTHORS];
}

/** Whether DCYFR drafted or co-drafted this post (frontmatter `authors` default is `['dcyfr']`). */
export function hasDcyfrByline(authors: string[] | undefined): boolean {
  return resolveAuthors(authors).includes('dcyfr');
}

/** Whether this is a joint Drew + DCYFR byline, as opposed to a DCYFR-solo post. */
export function isJointByline(authors: string[] | undefined): boolean {
  const list = resolveAuthors(authors);
  return list.includes('dcyfr') && list.includes('drew');
}

function formatModel(credits: PostCredits | undefined): string | undefined {
  if (!credits?.model) return undefined;
  return credits.provider ? `${credits.model} (${credits.provider})` : credits.model;
}

/**
 * Build the provenance footer as an array of standalone sentences (the
 * component joins them with spaces). Never describes AI images with a craft
 * verb ("illustrated", "painted") — it names the model instead, per house
 * rule.
 */
export function buildCreditsLines(post: Pick<Post, 'authors' | 'credits'>): string[] {
  const authors = resolveAuthors(post.authors);
  const credits = post.credits;
  const lines: string[] = [];

  if (authors.includes('dcyfr')) {
    const joint = authors.includes('drew');
    const modelPart = formatModel(credits);

    if (modelPart) {
      lines.push(
        joint
          ? `Drafted by DCYFR with ${modelPart}, alongside Drew's own words.`
          : `Drafted by DCYFR with ${modelPart}.`
      );
    } else {
      lines.push(joint ? "Drafted by DCYFR, alongside Drew's own words." : 'Drafted by DCYFR.');
    }

    if (credits?.image_model) {
      lines.push(
        credits.image_provider
          ? `Hero image: ${credits.image_model} via ${credits.image_provider}.`
          : `Hero image: ${credits.image_model}.`
      );
    }

    lines.push(joint ? 'Reviewed and approved by Drew.' : 'Approved by Drew before publishing.');
  } else if (authors.includes('drew') && credits?.feedback === 'dcyfr') {
    lines.push('Feedback from DCYFR.');
  }

  return lines;
}

/** Sources cited in the credits block (research the drafting run pulled from). */
export function creditsSources(
  credits: PostCredits | undefined
): NonNullable<PostCredits['sources']> {
  return credits?.sources ?? [];
}
